import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowLeft, UserPlus, Shield, HardHat, Copy, RefreshCw, Trash2,
  KeyRound, Link2, Check, X,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { StatusPill, Btn } from '../components/admin/cockpit/Cockpit';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { toast } from 'sonner';

const ROLE_ADMIN = 'admin';
const ROLE_CREW = 'crew';

export default function UsersManagementPage({ axiosConfig, backendUrl, galleries = [] }) {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  // Add-user dialog state
  const [showAdd, setShowAdd] = useState(false);
  const [newRole, setNewRole] = useState(ROLE_CREW);
  const [newEmail, setNewEmail] = useState('');
  const [newName, setNewName] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newGalleryIds, setNewGalleryIds] = useState([]);
  const [saving, setSaving] = useState(false);

  // Edit assignments dialog
  const [assignUser, setAssignUser] = useState(null);
  const [assignIds, setAssignIds] = useState([]);

  // Reset password dialog
  const [pwUser, setPwUser] = useState(null);
  const [pwValue, setPwValue] = useState('');

  const galleryNameById = useCallback(
    (id) => galleries.find((g) => g.id === id)?.name || id,
    [galleries],
  );

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${backendUrl}/api/users`, axiosConfig);
      setUsers(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  }, [backendUrl]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const copy = (text, label = 'Link') => {
    navigator.clipboard.writeText(text)
      .then(() => toast.success(`${label} copied!`))
      .catch(() => toast.error('Failed to copy'));
  };

  const resetAddForm = () => {
    setNewRole(ROLE_CREW);
    setNewEmail('');
    setNewName('');
    setNewPassword('');
    setNewGalleryIds([]);
  };

  const handleCreate = async () => {
    const email = newEmail.trim().toLowerCase();
    if (!email) return toast.error('Email is required');
    if (newRole === ROLE_ADMIN && (!newPassword || newPassword.length < 6)) {
      return toast.error('Admin users need a password of at least 6 characters');
    }
    try {
      setSaving(true);
      const { data } = await axios.post(`${backendUrl}/api/users`, {
        username: email,
        name: newName.trim() || null,
        role: newRole,
        password: newPassword || null,
        assigned_gallery_ids: newRole === ROLE_CREW ? newGalleryIds : [],
      }, axiosConfig);
      toast.success('User created');
      if (data.role === ROLE_CREW && data.magic_link) {
        copy(data.magic_link, 'Magic link');
      }
      setShowAdd(false);
      resetAddForm();
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create user');
    } finally {
      setSaving(false);
    }
  };

  const openAssign = (user) => {
    setAssignUser(user);
    setAssignIds(user.assigned_gallery_ids || []);
  };

  const saveAssignments = async () => {
    try {
      await axios.put(`${backendUrl}/api/users/${assignUser.id}`,
        { assigned_gallery_ids: assignIds }, axiosConfig);
      toast.success('Gallery assignments updated');
      setAssignUser(null);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update assignments');
    }
  };

  const regenerateMagicLink = async (user) => {
    try {
      const { data } = await axios.post(`${backendUrl}/api/users/${user.id}/magic-link`, {}, axiosConfig);
      copy(data.magic_link, 'New magic link');
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to regenerate link');
    }
  };

  const toggleActive = async (user) => {
    try {
      await axios.put(`${backendUrl}/api/users/${user.id}`, { active: !user.active }, axiosConfig);
      toast.success(user.active ? 'User disabled' : 'User enabled');
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update user');
    }
  };

  const resetPassword = async () => {
    if (!pwValue || pwValue.length < 6) return toast.error('Password must be at least 6 characters');
    try {
      await axios.put(`${backendUrl}/api/users/${pwUser.id}`, { password: pwValue }, axiosConfig);
      toast.success('Password updated');
      setPwUser(null);
      setPwValue('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reset password');
    }
  };

  const deleteUser = async (user) => {
    if (!window.confirm(`Delete ${user.username}? This cannot be undone.`)) return;
    try {
      await axios.delete(`${backendUrl}/api/users/${user.id}`, axiosConfig);
      toast.success('User deleted');
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete user');
    }
  };

  const GalleryChecklist = ({ selectedIds, onToggle }) => (
    <div className="max-h-56 overflow-y-auto border border-slate-200 dark:border-slate-700 rounded-lg divide-y divide-slate-100 dark:divide-slate-700">
      {galleries.length === 0 && (
        <p className="p-3 text-sm text-slate-500">No galleries available.</p>
      )}
      {galleries.map((g) => {
        const checked = selectedIds.includes(g.id);
        return (
          <label key={g.id} className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-700/50">
            <input
              type="checkbox"
              checked={checked}
              onChange={() => onToggle(g.id)}
              className="h-4 w-4 accent-[#BF212F]"
            />
            <span className="text-sm text-slate-700 dark:text-slate-200 flex-1 truncate">{g.name}</span>
            <span className="text-xs text-slate-400">{g.photo_count ?? 0} photos</span>
          </label>
        );
      })}
    </div>
  );

  return (
    <div className="font-tac-body">
      {/* Header */}
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/')} className="font-tac-heading inline-flex items-center gap-1" style={{ fontSize: 13, color: '#55555C', fontWeight: 600, background: 'transparent', border: 'none', cursor: 'pointer' }}>
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
          <div>
            <h1 className="font-tac-display" style={{ fontWeight: 800, fontSize: 24, color: '#141414', letterSpacing: '-0.015em' }}>Users</h1>
            <p style={{ fontSize: 13, color: '#55555C' }}>Manage administrators and crew access.</p>
          </div>
        </div>
        <Btn variant="primary" onClick={() => { resetAddForm(); setShowAdd(true); }}>
          <UserPlus className="w-4 h-4" /> Add User
        </Btn>
      </div>

      {loading ? (
        <div className="flex justify-center py-20">
          <div className="animate-spin rounded-full h-10 w-10" style={{ border: '3px solid rgba(191,33,47,0.2)', borderTopColor: '#BF212F' }} />
        </div>
      ) : (
        <div className="space-y-3">
          {users.map((user) => {
            const isCrew = user.role === ROLE_CREW;
            return (
              <div key={user.id} style={{ background: '#fff', border: '1px solid #E4E4E9', borderRadius: 14, padding: 16 }}>
                <div className="flex flex-col md:flex-row md:items-center gap-3 md:gap-6">
                  {/* Identity */}
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className="flex items-center justify-center text-white" style={{ width: 40, height: 40, borderRadius: 11, background: isCrew ? '#2563EB' : '#2B2B2B' }}>
                      {isCrew ? <HardHat className="w-5 h-5" /> : <Shield className="w-5 h-5" />}
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-tac-heading truncate" style={{ fontWeight: 700, color: '#141414' }}>
                          {user.name || user.username}
                        </span>
                        <StatusPill tone={isCrew ? 'info' : 'neutral'}>{isCrew ? 'Crew' : 'Admin'}</StatusPill>
                        {!user.active && <StatusPill tone="cancel">Disabled</StatusPill>}
                      </div>
                      {user.name && <div style={{ fontSize: 13, color: '#9A9AA0' }} className="truncate">{user.username}</div>}
                      {isCrew && (
                        <div style={{ fontSize: 12, color: '#9A9AA0', marginTop: 2 }}>
                          {(user.assigned_gallery_ids || []).length} galleries:{' '}
                          {(user.assigned_gallery_ids || []).slice(0, 3).map(galleryNameById).join(', ')}
                          {(user.assigned_gallery_ids || []).length > 3 ? '…' : ''}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex flex-wrap items-center gap-2">
                    {isCrew && user.magic_link && (
                      <Btn variant="secondary" size="sm" onClick={() => copy(user.magic_link, 'Magic link')}><Copy className="w-3.5 h-3.5" /> Copy link</Btn>
                    )}
                    {isCrew && (
                      <>
                        <Btn variant="secondary" size="sm" onClick={() => openAssign(user)}><Link2 className="w-3.5 h-3.5" /> Galleries</Btn>
                        <Btn variant="secondary" size="sm" onClick={() => regenerateMagicLink(user)}><RefreshCw className="w-3.5 h-3.5" /> New link</Btn>
                      </>
                    )}
                    {!isCrew && (
                      <Btn variant="secondary" size="sm" onClick={() => { setPwUser(user); setPwValue(''); }}><KeyRound className="w-3.5 h-3.5" /> Password</Btn>
                    )}
                    <Btn variant="secondary" size="sm" onClick={() => toggleActive(user)}>
                      {user.active ? <X className="w-3.5 h-3.5" /> : <Check className="w-3.5 h-3.5" />}
                      {user.active ? 'Disable' : 'Enable'}
                    </Btn>
                    <Btn variant="secondary" size="sm" style={{ color: '#BF212F' }} onClick={() => deleteUser(user)}><Trash2 className="w-3.5 h-3.5" /></Btn>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Add user dialog */}
      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent className="bg-white dark:bg-slate-800">
          <DialogHeader>
            <DialogTitle>Add User</DialogTitle>
            <DialogDescription>
              Admins have full access. Crew are limited to assigned galleries (upload, share links, email links, delete photos).
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            {/* Role toggle */}
            <div className="grid grid-cols-2 gap-2">
              {[ROLE_CREW, ROLE_ADMIN].map((r) => (
                <button
                  key={r}
                  type="button"
                  onClick={() => setNewRole(r)}
                  className={`flex items-center justify-center gap-2 py-2 rounded-lg border text-sm font-medium transition-colors ${
                    newRole === r
                      ? 'border-[#BF212F] bg-red-50 text-[#BF212F] dark:bg-red-900/20'
                      : 'border-slate-200 dark:border-slate-600 text-slate-600 dark:text-slate-300'
                  }`}
                >
                  {r === ROLE_CREW ? <HardHat className="w-4 h-4" /> : <Shield className="w-4 h-4" />}
                  {r === ROLE_CREW ? 'Crew' : 'Admin'}
                </button>
              ))}
            </div>

            <div className="space-y-2">
              <Label htmlFor="user-email">Email</Label>
              <Input id="user-email" type="email" placeholder="name@example.com"
                value={newEmail} onChange={(e) => setNewEmail(e.target.value)} />
            </div>

            <div className="space-y-2">
              <Label htmlFor="user-name">Name (optional)</Label>
              <Input id="user-name" placeholder="Jane Smith"
                value={newName} onChange={(e) => setNewName(e.target.value)} />
            </div>

            {newRole === ROLE_ADMIN ? (
              <div className="space-y-2">
                <Label htmlFor="user-pw">Password</Label>
                <Input id="user-pw" type="password" placeholder="At least 6 characters"
                  value={newPassword} onChange={(e) => setNewPassword(e.target.value)} />
              </div>
            ) : (
              <div className="space-y-2">
                <Label>Assigned galleries</Label>
                <GalleryChecklist
                  selectedIds={newGalleryIds}
                  onToggle={(id) => setNewGalleryIds((prev) =>
                    prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id])}
                />
                <p className="text-xs text-slate-500">A magic login link will be generated and copied to your clipboard.</p>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAdd(false)}>Cancel</Button>
            <Button onClick={handleCreate} disabled={saving} className="text-white"
              style={{ backgroundColor: '#BF212F' }}>
              {saving ? 'Creating…' : 'Create User'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit assignments dialog */}
      <Dialog open={!!assignUser} onOpenChange={(open) => !open && setAssignUser(null)}>
        <DialogContent className="bg-white dark:bg-slate-800">
          <DialogHeader>
            <DialogTitle>Assigned Galleries</DialogTitle>
            <DialogDescription>{assignUser?.name || assignUser?.username}</DialogDescription>
          </DialogHeader>
          <GalleryChecklist
            selectedIds={assignIds}
            onToggle={(id) => setAssignIds((prev) =>
              prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id])}
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setAssignUser(null)}>Cancel</Button>
            <Button onClick={saveAssignments} className="text-white"
              style={{ backgroundColor: '#BF212F' }}>
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset password dialog */}
      <Dialog open={!!pwUser} onOpenChange={(open) => !open && setPwUser(null)}>
        <DialogContent className="bg-white dark:bg-slate-800">
          <DialogHeader>
            <DialogTitle>Reset Password</DialogTitle>
            <DialogDescription>{pwUser?.name || pwUser?.username}</DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <Label htmlFor="reset-pw">New password</Label>
            <Input id="reset-pw" type="password" placeholder="At least 6 characters"
              value={pwValue} onChange={(e) => setPwValue(e.target.value)} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPwUser(null)}>Cancel</Button>
            <Button onClick={resetPassword} className="text-white"
              style={{ backgroundColor: '#BF212F' }}>
              Update
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
